import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export interface LohnausweisData {
  employee: {
    name: string;
    firstname?: string;
    lastname?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    ahv_number?: string;
    birth_date?: string;
  };
  company: {
    name: string;
    address: string;
    city: string;
    postal_code: string;
  };
  period: {
    start_date: string;
    end_date: string;
  };
  totals: {
    total_gross: number;
    total_net: number;
    total_deductions: number;
    total_hours: number;
    period_count: number;
    ahv_total: number;
    alv_total: number;
    bvg_total: number;
    nbu_total: number;
    ktg_total: number;
    tax_total: number;
  };
}

const BRAND_TEAL: [number, number, number] = [45, 212, 191];
const BRAND_TEAL_LIGHT: [number, number, number] = [240, 253, 250];

export function generateLohnausweis(data: LohnausweisData): Blob {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const margin = 20;
  const rightCol = pageWidth / 2 + 5;
  let y = 20;

  // --- Header ---
  pdf.setFillColor(...BRAND_TEAL);
  pdf.rect(0, 0, pageWidth, 35, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('LOHNAUSWEIS', pageWidth / 2, 15, { align: 'center' });

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  const periodStr = `Zeitraum: ${format(new Date(data.period.start_date), 'dd.MM.yyyy')} - ${format(new Date(data.period.end_date), 'dd.MM.yyyy')}`;
  pdf.text(periodStr, pageWidth / 2, 25, { align: 'center' });

  pdf.setFontSize(9);
  pdf.text('Formular 11 (Schweiz)', pageWidth / 2, 32, { align: 'center' });

  pdf.setTextColor(0, 0, 0);
  y = 45;

  // --- Employer / Employee split ---
  pdf.setFillColor(...BRAND_TEAL_LIGHT);
  pdf.rect(margin, y, pageWidth - 2 * margin, 40, 'F');

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ARBEITGEBER', margin + 5, y + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.company.name, margin + 5, y + 15);
  pdf.text(data.company.address, margin + 5, y + 21);
  pdf.text(`${data.company.postal_code} ${data.company.city}`, margin + 5, y + 27);

  pdf.setFont('helvetica', 'bold');
  pdf.text('ARBEITNEHMER', rightCol, y + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.employee.name, rightCol, y + 15);
  if (data.employee.address) {
    pdf.text(data.employee.address, rightCol, y + 21);
  }
  const cityLine = [data.employee.postal_code, data.employee.city].filter(Boolean).join(' ');
  if (cityLine) {
    pdf.text(cityLine, rightCol, y + 27);
  }
  if (data.employee.ahv_number) {
    pdf.text(`AHV-Nr: ${data.employee.ahv_number}`, rightCol, y + 33);
  }
  if (data.employee.birth_date) {
    pdf.text(`Geb.: ${format(new Date(data.employee.birth_date), 'dd.MM.yyyy')}`, rightCol + 60, y + 33);
  }

  y += 48;

  // --- Numbered boxes ---
  const drawLine = (num: number, label: string, value: string, bold = false) => {
    if (bold) pdf.setFont('helvetica', 'bold');
    else pdf.setFont('helvetica', 'normal');

    pdf.setFontSize(9);
    pdf.text(`${num}.`, margin + 2, y);
    pdf.text(label, margin + 12, y);
    pdf.text(value, pageWidth - margin - 5, y, { align: 'right' });

    // Light separator
    pdf.setDrawColor(220, 220, 220);
    pdf.line(margin, y + 2, pageWidth - margin, y + 2);
    y += 7;
  };

  const drawSectionHeader = (title: string) => {
    pdf.setFillColor(...BRAND_TEAL);
    pdf.rect(margin, y - 4, pageWidth - 2 * margin, 7, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin + 5, y + 1);
    pdf.setTextColor(0, 0, 0);
    y += 8;
  };

  const chf = (val: number) => `CHF ${val.toFixed(2)}`;

  // EINKOMMEN section
  drawSectionHeader('EINKOMMEN');

  drawLine(1, 'Lohn (inkl. Zulagen)', chf(data.totals.total_gross), true);
  drawLine(2, 'Gehaltsnebenleistungen', chf(0));
  drawLine(3, 'Unentgeltliche Befoerderung', 'Nein');
  drawLine(4, 'Kantinenverpflegung', 'Nein');
  drawLine(5, 'Beteiligungsrechte', 'Nein');
  drawLine(6, 'Verwaltungsratsentschaedigungen', 'Nein');
  drawLine(7, 'Uebrige Leistungen', chf(0));

  y += 3;
  drawLine(8, 'Bruttolohn Total', chf(data.totals.total_gross), true);

  y += 5;

  // ABZUEGE section
  drawSectionHeader('ABZUEGE');

  drawLine(9, 'AHV/IV/EO-Beitraege', chf(data.totals.ahv_total));
  drawLine(10, 'ALV-Beitraege', chf(data.totals.alv_total));
  drawLine(11, 'NBU-Beitraege', chf(data.totals.nbu_total));
  drawLine(12, 'BVG-Beitraege (Pensionskasse)', chf(data.totals.bvg_total));

  const weitereAbzuege = data.totals.ktg_total + data.totals.tax_total;
  drawLine(13, 'Weitere Abzuege (KTG, Quellensteuer)', chf(weitereAbzuege));

  y += 5;

  // NETTOLOHN
  drawSectionHeader('NETTOLOHN');
  drawLine(14, 'Nettolohn', chf(data.totals.total_net), true);

  y += 3;
  drawLine(15, 'Arbeitsstunden Total', `${data.totals.total_hours.toFixed(1)} h`);

  // --- Footer ---
  y += 10;
  pdf.setDrawColor(...BRAND_TEAL);
  pdf.setLineWidth(0.5);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Erstellt am: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, margin, y);
  pdf.text(`Anzahl Lohnperioden: ${data.totals.period_count}`, pageWidth - margin, y, { align: 'right' });

  y += 6;
  pdf.setFont('helvetica', 'italic');
  pdf.text('Dieser Lohnausweis entspricht den schweizerischen Bestimmungen (Formular 11).', pageWidth / 2, y, { align: 'center' });

  return pdf.output('blob');
}

export function downloadLohnausweis(blob: Blob, employeeName: string, periodStart: string, periodEnd: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const start = format(new Date(periodStart), 'yyyyMMdd');
  const end = format(new Date(periodEnd), 'yyyyMMdd');
  link.download = `Lohnausweis_${employeeName.replace(/\s+/g, '_')}_${start}-${end}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
